import React, { HTMLAttributes, useCallback } from 'react'
import LogOutIcon from 'transfix-icons/build/LogOutIcon'
import { makeStyles, Drawer, DrawerClassKey, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useModalState } from 'utils/hooks/useModalState'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { INavigationRoute } from '../NavigationBar/types'
import MobileMenu from './components/MobileMenu'
import SignOutModal from './components/SignOutModal'

const NAVBAR_HEIGHT_PX = 48
const DRAWER_WIDTH_PX = 270

export interface INavigationMobileDrawerProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Default state when page renders
   * @default false
   */
  isOpen?: boolean
  onClose: () => void
  routes: INavigationRoute[]
}

interface IUseSideNavDrawerStylesProps {
  /**
   * @default true
   */
  isNavExpanded?: boolean
}

const useNavigationMobileDrawerStyles = makeStyles(theme => {
  return createStyles<
    Extract<DrawerClassKey, 'root' | 'paper' | 'modal'>,
    IUseSideNavDrawerStylesProps
  >({
    root: {
      '&.MuiDrawer-root': {
        width: DRAWER_WIDTH_PX,
        transition: `all 150ms ${theme.transitions.easing.sharp}`,
      },
    },
    paper: {
      '&.MuiDrawer-paper': {
        top: NAVBAR_HEIGHT_PX,
        width: DRAWER_WIDTH_PX,
        transition: `all 150ms ${theme.transitions.easing.sharp}`,
        borderRight: `1px solid ${brandColors.coolGray7}`,
      },
    },
    modal: {
      '&.MuiModal-backdrop': {
        top: NAVBAR_HEIGHT_PX,
      },
    },
  })
})

const getMobileRoutes = (
  routes: INavigationRoute[],
  options: { onClickSignOut: () => void }
): INavigationRoute[] => {
  const { onClickSignOut } = options || {}
  return [
    ...routes,
    {
      name: 'Sign Out',
      location: 'signout',
      Icon: LogOutIcon,
      dividerBefore: true,
      variant: 'error',
      onClick: onClickSignOut,
      disableLink: true,
    },
  ]
}

const NavigationMobileDrawer = ({
  onClose,
  isOpen = false,
  routes: routesProp = [],
}: INavigationMobileDrawerProps) => {
  const { logout } = useUserDetails()
  const { isMobile } = useBreakpointContext()
  const { modalState, toggleModal } = useModalState({ signOutModal: false })

  const closeModalHandler = () => toggleModal('signOutModal', false)

  const openModalHandler = useCallback(() => {
    if (isMobile) {
      logout()
    } else {
      toggleModal('signOutModal', true)
    }
  }, [isMobile, logout, toggleModal])

  const classes = useNavigationMobileDrawerStyles({ isNavExpanded: isOpen })

  const routes = getMobileRoutes(routesProp, { onClickSignOut: openModalHandler })

  return (
    <>
      <Drawer
        data-test='NavigationMobileDrawer'
        variant='temporary'
        anchor='right'
        open={isOpen}
        classes={classes}
        onClose={onClose}
        onBackdropClick={onClose}
        ModalProps={{
          onBackdropClick: onClose,
          /**
           * @note When this value is true, it triggers
           * some unexpected behavior with ApolloProvider
           */
          keepMounted: false,
          BackdropProps: {
            className: classes.modal,
          },
        }}>
        <MobileMenu routes={routes} onClose={onClose} />
      </Drawer>
      {modalState.signOutModal && <SignOutModal onClose={closeModalHandler} />}
    </>
  )
}

export default NavigationMobileDrawer
